import React, { useEffect, useMemo, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import {
  FaCalendarAlt,
  FaChartBar,
  FaChevronDown,
  FaChevronUp,
  FaEnvelope,
  FaHandsHelping,
  FaHome,
  FaImage,
  FaLayerGroup,
  FaMapSigns,
  FaMoon,
  FaNewspaper,
  FaSearch,
  FaSignOutAlt,
  FaSun,
  FaUserShield,
  FaUsers,
} from "react-icons/fa";
import { GiPodiumWinner } from "react-icons/gi";
import { IoMdSettings } from "react-icons/io";
import { IoSettings } from "react-icons/io5";
import { BsArrowLeftShort } from "react-icons/bs";

import { useAsideMenu } from "../../contexts/AsideMenuContext";
import { getAllSections } from "../../services/sectionsServices";

const DEFAULT_AVATAR = "/images/user_avatar.jpg";
const API_BASE = process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "";

const SECTION_MENUS = [
  {
    title: "Messages",
    path: "/appli/messages",
    icon: FaEnvelope,
  },
  {
    title: "Actualités",
    path: "/appli/actualites",
    icon: FaNewspaper,
  },
  {
    title: "Albums photos",
    path: "/appli/albums-photos",
    icon: FaImage,
  },
  {
    title: "Groupes",
    path: "/appli/groupes",
    icon: FaUsers,
  },
  {
    title: "Agenda",
    path: "/appli/agenda",
    icon: FaCalendarAlt,
  },
  {
    title: "Palmarès",
    path: "/appli/palmares",
    icon: GiPodiumWinner,
  },
  {
    title: "Association",
    path: "/appli/section",
    icon: IoMdSettings,
  },
];

const ADMIN_MENUS = [
  {
    title: "Utilisateurs",
    path: "/appli/utilisateurs",
    icon: FaUserShield,
  },
  {
    title: "Associations admin",
    path: "/appli/sectionadmin",
    icon: FaLayerGroup,
  },
  {
    title: "Infrastructures",
    path: "/appli/infrastructures",
    icon: FaMapSigns,
  },
  {
    title: "Partenaires",
    path: "/appli/partenaires",
    icon: FaHandsHelping,
  },
  {
    title: "Equipes",
    path: "/appli/equipes",
    icon: FaUsers,
  },
  {
    title: "Paramètres admin",
    path: "/appli/settingsadmin",
    icon: IoSettings,
  },
  {
    title: "Visites",
    path: "/appli/visites",
    icon: FaChartBar,
  },
];

const getRoleName = (roleId) => {
  switch (Number(roleId)) {
    case 1:
      return "Administrateur";
    case 2:
      return "Responsable association";
    case 3:
      return "Démonstration";
    default:
      return "Utilisateur";
  }
};

function isExternalURL(url) {
  try {
    const parsed = new URL(url);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
}

function resolveMediaUrl(url, fallback = "/images/asso_placeholder.webp") {
  if (!url) return fallback;

  const value = String(url).trim();

  if (
    isExternalURL(value) ||
    value.startsWith("data:") ||
    value.startsWith("blob:")
  ) {
    return value;
  }

  if (value.startsWith("/")) {
    return `${API_BASE}${value}`;
  }

  return value;
}

function normalizeText(value = "") {
  return String(value)
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

const AsideMenu = ({
  userInfo,
  sectionInfo,
  activeSectionId,
  onActiveSectionChange,
}) => {
  const { isOpen, toggleMenu, closeMenu } = useAsideMenu();
  const location = useLocation();

  const [isDarkMode, setIsDarkMode] = useState(false);
  const [sections, setSections] = useState([]);
  const [sectionsLoading, setSectionsLoading] = useState(false);
  const [sectionQuery, setSectionQuery] = useState("");
  const [isSectionSwitcherOpen, setSectionSwitcherOpen] = useState(true);

  const user = useMemo(() => {
    return (
      userInfo ||
      JSON.parse(localStorage.getItem("user") || "null") || {
        nom: "",
        prenom: "",
        email: "",
        photoURL: "",
        role_id: 0,
        section_id: 0,
      }
    );
  }, [userInfo]);

  const isAdmin = Number(user?.role_id) === 1;

  const selectedSection = useMemo(() => {
    return sections.find(
      (section) => String(section.id) === String(activeSectionId)
    );
  }, [sections, activeSectionId]);

  const filteredSections = useMemo(() => {
    const q = normalizeText(sectionQuery.trim());

    return sections.filter((section) => {
      if (!q) return true;

      return normalizeText(
        [
          section.nom_section,
          section.discipline_section,
          section.responsable_section,
        ]
          .filter(Boolean)
          .join(" ")
      ).includes(q);
    });
  }, [sections, sectionQuery]);

  useEffect(() => {
    const theme = localStorage.getItem("theme");

    if (theme === "dark") {
      document.documentElement.classList.add("dark");
      setIsDarkMode(true);
    }
  }, []);

  useEffect(() => {
    if (!isAdmin) return;

    let mounted = true;

    const fetchSections = async () => {
      try {
        setSectionsLoading(true);

        const data = await getAllSections();
        const list = Array.isArray(data) ? data : [];

        const sorted = list
          .filter((section) => section?.id)
          .sort((a, b) =>
            String(a.nom_section || "").localeCompare(
              String(b.nom_section || ""),
              "fr",
              { sensitivity: "base" }
            )
          );

        if (!mounted) return;

        setSections(sorted);

        const hasActiveSection = sorted.some(
          (section) => String(section.id) === String(activeSectionId)
        );

        if (!hasActiveSection && sorted.length > 0) {
          const storedId = localStorage.getItem("admin_active_section_id");

          const storedSection = sorted.find(
            (section) => String(section.id) === String(storedId)
          );

          onActiveSectionChange?.(storedSection || sorted[0]);
        }
      } catch (error) {
        console.error("Erreur chargement associations menu :", error);
      } finally {
        if (mounted) setSectionsLoading(false);
      }
    };

    fetchSections();

    return () => {
      mounted = false;
    };
  }, [isAdmin, activeSectionId, onActiveSectionChange]);

  const toggleTheme = () => {
    const nextIsDark = !isDarkMode;

    setIsDarkMode(nextIsDark);
    document.documentElement.classList.toggle("dark", nextIsDark);
    localStorage.setItem("theme", nextIsDark ? "dark" : "light");
  };

  const handleLogout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    localStorage.removeItem("admin_active_section_id");
    window.location.href = "/login";
  };

  const handleSectionClick = (section) => {
    onActiveSectionChange?.(section);
  };

  const headerLogo =
    selectedSection?.logo_section ||
    selectedSection?.photoURL ||
    sectionInfo?.logo ||
    "";

  const headerName =
    selectedSection?.nom_section || sectionInfo?.nom || "MonAppliAsso";

  const headerDiscipline =
    selectedSection?.discipline_section ||
    sectionInfo?.discipline ||
    "Administration";

  return (
    <>
      {isOpen && (
        <button
          type="button"
          aria-label="Fermer le menu"
          onClick={closeMenu}
          className="fixed inset-0 z-30 bg-slate-950/50 backdrop-blur-sm lg:hidden"
        />
      )}

      <aside
        className={[
          "fixed inset-y-0 left-0 z-40 flex flex-col border-r border-white/10 bg-slate-950 text-white shadow-2xl transition-all duration-300",
          isOpen
            ? "w-72 translate-x-0"
            : "-translate-x-full lg:w-20 lg:translate-x-0",
        ].join(" ")}
      >
        <div className="relative flex min-h-0 flex-1 flex-col">
          <div className="flex h-20 items-center gap-3 border-b border-white/10 px-4">
            <Link
              to="/appli/messages"
              className="flex min-w-0 flex-1 items-center gap-3"
            >
              <div className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-white p-1 shadow">
                {headerLogo ? (
                  <img
                    src={resolveMediaUrl(headerLogo)}
                    alt={headerName}
                    className="h-full w-full object-contain"
                    onError={(event) => {
                      event.currentTarget.src = "/images/asso_placeholder.webp";
                    }}
                  />
                ) : (
                  <span className="text-lg font-bold text-primary">A</span>
                )}
              </div>

              {isOpen && (
                <div className="min-w-0">
                  <p className="truncate text-sm font-bold text-white">
                    {headerName}
                  </p>
                  <p className="truncate text-xs text-white/50">
                    {headerDiscipline}
                  </p>
                </div>
              )}
            </Link>

            <button
              type="button"
              onClick={toggleMenu}
              className="absolute -right-4 top-6 hidden h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-900 shadow-lg transition hover:scale-105 lg:flex"
              aria-label="Réduire ou ouvrir le menu"
            >
              <BsArrowLeftShort
                size={26}
                className={!isOpen ? "rotate-180 transition" : "transition"}
              />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-3 py-5">
            {isAdmin && isOpen && (
              <AdminSectionSwitcher
                sections={filteredSections}
                selectedSectionId={activeSectionId}
                query={sectionQuery}
                loading={sectionsLoading}
                isOpen={isSectionSwitcherOpen}
                onToggle={() => setSectionSwitcherOpen((current) => !current)}
                onQueryChange={setSectionQuery}
                onSectionClick={handleSectionClick}
              />
            )}

            <MenuGroup title="Association active" isOpen={isOpen}>
              {SECTION_MENUS.map((menu) => (
                <MenuItem
                  key={menu.path}
                  menu={{
                    ...menu,
                    state: { sectionId: activeSectionId },
                  }}
                  isOpen={isOpen}
                  currentPath={location.pathname}
                />
              ))}
            </MenuGroup>

            {isAdmin && (
              <MenuGroup title="Administration globale" isOpen={isOpen}>
                {ADMIN_MENUS.map((menu) => (
                  <MenuItem
                    key={menu.path}
                    menu={menu}
                    isOpen={isOpen}
                    currentPath={location.pathname}
                  />
                ))}
              </MenuGroup>
            )}

            <MenuGroup title="Accès rapide" isOpen={isOpen}>
              <MenuItem
                menu={{
                  title: "Retour au site",
                  path: "/",
                  icon: FaHome,
                }}
                isOpen={isOpen}
                currentPath={location.pathname}
              />

              <button
                type="button"
                onClick={toggleTheme}
                className="group mt-1 flex w-full items-center gap-3 rounded-2xl px-3 py-3 text-sm font-bold text-white/70 transition hover:bg-white/10 hover:text-white"
              >
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/10 text-lg">
                  {isDarkMode ? (
                    <FaSun className="text-yellow-300" />
                  ) : (
                    <FaMoon />
                  )}
                </span>

                {isOpen && (
                  <span className="truncate text-left">
                    {isDarkMode ? "Mode clair" : "Mode sombre"}
                  </span>
                )}
              </button>
            </MenuGroup>
          </div>

          <div className="border-t border-white/10 p-3">
            <div className="flex items-center gap-3 rounded-2xl bg-white/5 p-3">
              <img
                src={resolveMediaUrl(user?.photoURL, DEFAULT_AVATAR)}
                alt={`${user?.prenom || ""} ${user?.nom || ""}`}
                className="h-11 w-11 shrink-0 rounded-2xl object-cover"
                onError={(event) => {
                  event.currentTarget.src = DEFAULT_AVATAR;
                }}
              />

              {isOpen && (
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-bold text-white">
                    {user?.prenom} {user?.nom}
                  </p>
                  <p className="truncate text-xs text-white/50">
                    {getRoleName(user?.role_id)}
                  </p>
                </div>
              )}

              {isOpen && (
                <button
                  type="button"
                  onClick={handleLogout}
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-white/50 transition hover:bg-red-500/10 hover:text-red-300"
                  title="Se déconnecter"
                >
                  <FaSignOutAlt />
                </button>
              )}
            </div>
          </div>
        </div>
      </aside>
    </>
  );
};

function AdminSectionSwitcher({
  sections,
  selectedSectionId,
  query,
  loading,
  isOpen,
  onToggle,
  onQueryChange,
  onSectionClick,
}) {
  return (
    <div className="mb-6 overflow-hidden rounded-3xl border border-white/10 bg-white/[0.04]">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between gap-3 px-3 py-3 text-left"
      >
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-white/40">
            Association à gérer
          </p>
          <p className="mt-0.5 text-sm font-bold text-white">
            Sélection admin
          </p>
        </div>

        <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-white/10 text-white/70">
          {isOpen ? <FaChevronUp /> : <FaChevronDown />}
        </span>
      </button>

      {isOpen && (
        <div className="border-t border-white/10 p-3">
          <div className="mb-3 flex items-center gap-2 rounded-2xl bg-slate-900 px-3 py-2 ring-1 ring-white/10">
            <FaSearch className="text-white/35" />

            <input
              type="text"
              value={query}
              onChange={(event) => onQueryChange(event.target.value)}
              placeholder="Rechercher..."
              className="w-full bg-transparent text-sm font-medium text-white outline-none placeholder:text-white/30"
            />
          </div>

          <div className="max-h-72 space-y-1 overflow-y-auto pr-1">
            {loading ? (
              <div className="space-y-2">
                {[...Array(4)].map((_, index) => (
                  <div
                    key={index}
                    className="h-12 animate-pulse rounded-2xl bg-white/10"
                  />
                ))}
              </div>
            ) : sections.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-white/10 px-3 py-4 text-center text-xs text-white/45">
                Aucune association trouvée.
              </div>
            ) : (
              sections.map((section) => {
                const active =
                  String(section.id) === String(selectedSectionId);

                return (
                  <button
                    key={section.id}
                    type="button"
                    onClick={() => onSectionClick(section)}
                    className={[
                      "flex w-full items-center gap-3 rounded-2xl px-3 py-2 text-left transition",
                      active
                        ? "bg-primary text-white shadow-lg shadow-primary/20"
                        : "text-white/70 hover:bg-white/10 hover:text-white",
                    ].join(" ")}
                  >
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-white p-1">
                      <img
                        src={resolveMediaUrl(
                          section.logo_section || section.photoURL
                        )}
                        alt={section.nom_section}
                        className="h-full w-full object-contain"
                        onError={(event) => {
                          event.currentTarget.src =
                            "/images/asso_placeholder.webp";
                        }}
                      />
                    </span>

                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-bold">
                        {section.nom_section}
                      </span>
                      <span className="block truncate text-xs opacity-60">
                        {section.discipline_section || "Association"}
                      </span>
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function MenuGroup({ title, isOpen, children }) {
  return (
    <div className="mb-6">
      {isOpen ? (
        <p className="mb-2 px-3 text-xs font-bold uppercase tracking-[0.18em] text-white/35">
          {title}
        </p>
      ) : (
        <div className="mx-auto mb-3 h-px w-10 bg-white/10" />
      )}

      <div className="space-y-1">{children}</div>
    </div>
  );
}

function MenuItem({ menu, isOpen, currentPath }) {
  const Icon = menu.icon;

  const active =
    currentPath === menu.path || currentPath.startsWith(`${menu.path}/`);

  return (
    <NavLink
      to={menu.path}
      state={menu.state}
      className={[
        "group relative flex items-center gap-3 rounded-2xl px-3 py-3 text-sm font-bold transition",
        active
          ? "bg-primary text-white shadow-lg shadow-primary/20"
          : "text-white/70 hover:bg-white/10 hover:text-white",
      ].join(" ")}
    >
      <span
        className={[
          "flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-lg transition",
          active ? "bg-white/20" : "bg-white/10 group-hover:bg-white/15",
        ].join(" ")}
      >
        <Icon />
      </span>

      {isOpen && <span className="truncate">{menu.title}</span>}

      {!isOpen && (
        <span className="pointer-events-none absolute left-[72px] z-50 hidden whitespace-nowrap rounded-xl bg-slate-900 px-3 py-2 text-xs font-bold text-white shadow-xl group-hover:block">
          {menu.title}
        </span>
      )}
    </NavLink>
  );
}

export default AsideMenu;